/** tm/legal/LegalToolbar.tsx — 작은 오른쪽 묶음: 회색 "새벽 4시 수집 · 04:12" + ⓘ(단계·과거 자료 풍선) | 「🔔 새 소식 n」 · 설정/지금 수집(super).
 *  데스크톱은 따로 줄을 만들지 않고 3열 첫 줄 오른쪽에 붙인다(정부지원사업처럼 열이 바로 시작). 모바일은 「필터」 버튼(left)과 한 줄.
 *  인증키 없음·호출 거부일 때만 한 줄 빨간 띠(LegalErrorBar). */
import { useState, type ReactNode } from 'react';
import { C, T } from '../components/ui';
import { btn, whenLabel, MUTED } from './legalTheme';
import { Spinner } from './legalUi';
import LegalBatchSteps from './LegalBatchSteps';
import type { LegalSummary } from './legalTypes';

interface Props {
  summary: LegalSummary | null; running: boolean; isSuper: boolean; mobile: boolean;
  alertsTotal: number; alertsOpen: boolean;
  onBell: () => void; onSettings: () => void; onRun: () => void;
}

/** 수집 문구 — 새벽 자동 수집이면 "새벽 4시 수집 · 04:12", 「지금 수집」으로 돌렸으면 "최근 수집 22:23 (수동) · 다음 새벽 4시" */
export function lastText(s: LegalSummary | null, running: boolean): { text: string; bad: boolean } {
  if (running) return { text: '수집 중…', bad: false };
  const batch = s?.batch?.lastRun || null;
  const run = s?.lastRun || null;
  const useRun = !!run?.finished_at && (!batch?.finished_at || Date.parse(run.finished_at) > Date.parse(batch.finished_at));
  const at = useRun ? run?.finished_at : batch?.finished_at || run?.started_at;
  const st = useRun ? run?.status : batch?.status || run?.status;
  const trig = (useRun ? run?.trigger : batch?.trigger) || '';
  if (!at) return { text: '새벽 4시 수집 · 아직 전', bad: false };
  const w = whenLabel(at).replace('오늘 ', '');
  const fail = st === 'error' ? ' 실패' : st === 'partial' ? ' 일부 실패' : '';
  return trig === 'manual'
    ? { text: `최근 수집 ${w} (수동)${fail} · 다음 새벽 4시`, bad: !!fail }
    : { text: `새벽 4시 수집 · ${w}${fail}`, bad: !!fail };
}

const small = { ...btn(), padding: '4px 10px', fontSize: T.sm } as const;

export default function LegalToolbar({ summary, running, isSuper, mobile, alertsTotal, alertsOpen, onBell, onSettings, onRun }: Props) {
  const [tip, setTip] = useState(false);
  const l = lastText(summary, running);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: T.sm, color: l.bad ? C.orange : MUTED, whiteSpace: 'nowrap' }}
        onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)}>
        {running && <span style={{ color: C.accent }}><Spinner /></span>}
        {!mobile && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{l.text}</span>}
        <button type="button" className="lw-f lw-m40" aria-label={`수집 상태: ${l.text}`} aria-expanded={tip} title="수집 단계별 결과" onClick={() => setTip(o => !o)}
          onFocus={() => setTip(true)} onBlur={() => setTip(false)} onKeyDown={e => { if (e.key === 'Escape') setTip(false); }}
          style={{ border: `1px solid ${C.borderDk}`, background: C.white, color: C.sub, borderRadius: '50%', width: mobile ? 40 : 20, height: mobile ? 40 : 20, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>i</button>
        {tip && <LegalBatchSteps summary={summary} />}
      </span>
      <span aria-hidden="true" style={{ width: 1, height: 18, background: C.border, margin: '0 2px' }} />
      <button type="button" className="lw-f" onClick={e => { e.stopPropagation(); onBell(); }} aria-haspopup="dialog" aria-expanded={alertsOpen} data-lw-bell="1"
        style={{ ...small, border: `1px solid ${alertsOpen ? C.accent : C.borderDk}`, color: alertsOpen ? C.accent : C.sub, fontVariantNumeric: 'tabular-nums' }}>
        🔔 새 소식 <b style={{ color: C.text }}>{alertsTotal}</b>
      </button>
      {isSuper && !mobile && <>
        <button type="button" className="lw-f" onClick={onSettings} style={small}>설정</button>
        <button type="button" className="lw-f" onClick={onRun} disabled={running} title="새벽 일괄 수집(법령·판례·해석·국회 법안·입법예고·국무회의 의결·뉴스)을 지금 한 번 더 실행합니다. 몇 분 걸립니다"
          style={{ ...small, opacity: running ? 0.6 : 1, cursor: running ? 'default' : 'pointer' }}>{running ? '수집 중' : '지금 수집'}</button>
      </>}
    </div>
  );
}

/** 모바일 한 줄: 왼쪽 「필터」 버튼 + 오른쪽 묶음 */
export function MobileBar({ left, children }: { left: ReactNode; children: ReactNode }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>{left}<span style={{ flex: 1 }} />{children}</div>;
}

/** 인증키 없음 · 법제처 호출 거부 — 일어났을 때만 한 줄 */
export function LegalErrorBar({ summary }: { summary: LegalSummary | null }) {
  if (!summary) return null;
  const msg = !summary.ocConfigured ? '법제처 OPEN API 인증키(OC)가 서버에 없어 법령·판례·해석을 모으지 못합니다'
    : summary.authError ? '법제처가 서버 호출을 거부했습니다 — OPEN API 마이페이지에서 서버 IP(3.39.166.95) 등록을 확인해 주세요' : '';
  if (!msg) return null;
  return <div role="alert" title={summary.lastRun?.error || undefined} style={{ padding: '6px 16px', background: C.redLt, color: C.red, fontSize: T.sm, borderBottom: `1px solid #FECACA`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }}>{msg}</div>;
}
